import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Video,
  Film,
  Camera,
  Clapperboard,
  Sparkles,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Building2,
  Sliders,
  Tv,
  Monitor,
  Music,
  Scissors,
  Wand2,
  Eye,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/services_/video-production")({
  head: () => ({
    meta: [
      {
        title:
          "Video Production Services — KMWAdvertising LLP | TVCs, Brand Films & Corporate Videos",
      },
      {
        name: "description",
        content:
          "End-to-End Video Production Services by KMWAdvertising LLP. Pre-production, cinematic filming, corporate films, TVCs, product videos, and post-production.",
      },
      {
        property: "og:title",
        content: "Video Production Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "High-quality video production that tells powerful brand stories across TV, digital, OTT, and social media platforms.",
      },
    ],
  }),
  component: VideoProductionServicePage,
});

export function VideoProductionServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const preProductionList = [
    "Creative Ideation",
    "Concept Development",
    "Scriptwriting",
    "Storyboarding",
    "Mood Boards",
    "Casting Support",
    "Location Scouting",
    "Production Planning",
    "Budgeting",
    "Shot Planning",
  ];

  const productionList = [
    "Corporate Shoots",
    "Television Commercials (TVC)",
    "Digital Commercials",
    "Product Videos",
    "Manufacturing Videos",
    "Factory Tours",
    "Event Coverage",
    "Drone Videography",
    "Interviews",
    "Testimonials",
    "Lifestyle Shoots",
    "Brand Films",
  ];

  const postProductionList = [
    "Professional Editing",
    "Motion Graphics",
    "Colour Grading",
    "Sound Design",
    "Voice-over Recording",
    "Music Licensing",
    "Visual Effects (VFX)",
    "AI-assisted Editing",
    "Subtitles & Closed Captions",
    "Multi-language Versions",
    "Platform Optimisation",
  ];

  const videoTypesList = [
    "Brand Films",
    "Corporate Videos",
    "Product Launch Videos",
    "Explainer Videos",
    "Recruitment Videos",
    "Manufacturing Videos",
    "Testimonial Videos",
    "CEO Messages",
    "Event Videos",
    "CSR Films",
    "Training Videos",
    "Educational Content",
    "Social Media Videos",
    "Advertising Commercials",
    "Documentary Films",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Video Production</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Video className="h-3.5 w-3.5" />
              End-to-End Commercial Video Production
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Video Production
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                High-Quality Video Production That Tells Powerful Brand Stories
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                Video has become one of the most influential communication formats across digital marketing, television, OTT platforms and social media. It enables businesses to educate, inspire, entertain and persuade audiences more effectively than almost any other medium.
              </p>
              <p className="text-sm sm:text-base text-slate-400">
                At KMWAdvertising LLP, we offer complete end-to-end video production services, managing every stage from concept development to final delivery.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                Our experienced creative team collaborates with directors, cinematographers, editors, scriptwriters, animators and digital strategists to produce visually compelling videos that align with your marketing goals.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#video-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Start Video Project</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#video-stages"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Production Stages</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Production Stages (Pre, Production, Post) */}
      <section id="video-stages" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x space-y-16">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h2 className="text-xs font-mono font-bold uppercase tracking-widest text-pink-400">
              Structured 3-Stage Workflow
            </h2>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white">
              End-to-End Production Process
            </h3>
            <p className="text-slate-300 text-sm sm:text-base">
              Every production is carefully managed through three seamless phases to guarantee cinematic quality and brand alignment.
            </p>
          </div>

          {/* Pre-Production */}
          <div className="p-8 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400">
                  <Clapperboard className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">Pre-Production</h4>
                  <p className="text-xs text-slate-400">Every successful video begins with meticulous planning.</p>
                </div>
              </div>
              <span className="text-xs font-mono font-bold uppercase text-pink-400 bg-pink-500/10 px-3 py-1 rounded-full border border-pink-500/20">
                Phase 01
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {preProductionList.map((item) => (
                <div key={item} className="p-3.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2.5">
                  <Check className="h-3.5 w-3.5 text-pink-400 shrink-0" />
                  <span className="text-xs font-medium text-slate-200">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Production */}
          <div className="p-8 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                  <Camera className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">Production</h4>
                  <p className="text-xs text-slate-400">Using professional equipment and experienced production crews, we capture cinematic visuals that reflect your brand's quality.</p>
                </div>
              </div>
              <span className="text-xs font-mono font-bold uppercase text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full border border-purple-500/20">
                Phase 02
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {productionList.map((item) => (
                <div key={item} className="p-3.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2.5">
                  <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                  <span className="text-xs font-medium text-slate-200">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Post-Production */}
          <div className="p-8 sm:p-10 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400">
                  <Scissors className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">Post-Production</h4>
                  <p className="text-xs text-slate-400">Post-production transforms raw footage into engaging visual stories.</p>
                </div>
              </div>
              <span className="text-xs font-mono font-bold uppercase text-rose-400 bg-rose-500/10 px-3 py-1 rounded-full border border-rose-500/20">
                Phase 03
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {postProductionList.map((item) => (
                <div key={item} className="p-3.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2.5">
                  <Check className="h-3.5 w-3.5 text-rose-400 shrink-0" />
                  <span className="text-xs font-medium text-slate-200">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-xs font-mono text-rose-300/90 italic pt-2">
              Each video is delivered in formats optimised for websites, YouTube, television, social media, OTT platforms and digital advertising.
            </p>
          </div>
        </div>
      </section>

      {/* Types of Videos We Produce */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Film className="h-3.5 w-3.5" />
              Versatile Video Portfolio
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Types of Videos We Produce
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              We produce customized video formats engineered for corporate communication, marketing campaigns, employee onboarding, and brand awareness.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-10">
            {videoTypesList.map((type) => (
              <div
                key={type}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition text-center text-xs font-bold text-white"
              >
                {type}
              </div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-rose-950/20 to-purple-950/30 border border-white/15">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-medium">
              Our objective is not simply to create visually appealing videos, but to produce content that supports brand awareness, customer engagement and measurable business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="video-form" className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Video Production Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Produce Your Next High-Impact Brand Film
              </h2>
              <p className="text-slate-300 text-sm">
                Discuss your script, location shoot requirements, or TVC concept with our senior directors.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Video Production Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our executive producer will connect with you within 24 hours to review your concept brief.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Siddharth Rao"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Crestline Media"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="siddharth@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Production Type Needed</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="tvc">Television Commercial (TVC) / OTT Commercial</option>
                    <option value="corporate">Corporate Brand Film / Company Profile</option>
                    <option value="product">Product Launch & Manufacturing Video</option>
                    <option value="event">Event Coverage & Factory Tour Shoot</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Project Overview / Concept</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about the key narrative, shoot location preference, duration, or deadlines..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request Video Production Brief</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
